import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatTeardropDots, Envelope } from "@phosphor-icons/react";

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/me/conversations");
        setConvs(data);
      } catch { /* */ }
      setListLoading(false);
    })();
  }, [user, loading, navigate]);

  if (!user) return null;

  const requests = convs.filter(c => c.is_request && !c.accepted && c.request_from !== user.user_id);
  const accepted = convs.filter(c => !(c.is_request && !c.accepted && c.request_from !== user.user_id));

  return (
    <div className="pt-20 max-w-3xl mx-auto px-5 lg:px-8 pb-24" data-testid="messages-page">
      <div className="mb-8">
        <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ MENSAGENS</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Mensagens</h1>
      </div>

      {listLoading ? (
        <div className="text-center font-mono-accent text-xs text-zinc-500 py-10">A CARREGAR...</div>
      ) : convs.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <Envelope size={42} weight="duotone" className="text-[#E63946] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem mensagens</div>
          <div className="text-sm text-zinc-500 mt-1">Inicia uma conversa a partir do perfil de outro membro.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {requests.length > 0 && (
            <section>
              <div className="font-mono-accent text-xs text-[#F59E0B] mb-3">◢ PEDIDOS DE MENSAGEM · {requests.length}</div>
              <div className="space-y-2">
                {requests.map(c => <ConvRow key={c.conversation_id} c={c} isRequest />)}
              </div>
            </section>
          )}
          {accepted.length > 0 && (
            <section>
              {requests.length > 0 && <div className="font-mono-accent text-xs text-zinc-500 mb-3">◢ CONVERSAS</div>}
              <div className="space-y-2">
                {accepted.map(c => <ConvRow key={c.conversation_id} c={c} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function ConvRow({ c, isRequest }) {
  return (
    <Link
      to={`/mensagens/${c.other_user.user_id}`}
      className="card-hover bg-[#121217] border border-white/5 rounded-xl p-4 flex items-center gap-3 block"
      data-testid={`conv-${c.conversation_id}`}
    >
      {c.other_user.picture ? (
        <img src={resolveImageUrl(c.other_user.picture)} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-base font-bold flex-shrink-0">
          {(c.other_user.name || "?")[0]?.toUpperCase()}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-display font-semibold truncate">{c.other_user.name || "Sem nome"}</div>
          {isRequest && <span className="text-[10px] bg-[#F59E0B]/15 text-[#F59E0B] px-2 py-0.5 rounded font-mono-accent">PEDIDO</span>}
        </div>
        <div className="text-sm text-zinc-400 truncate">{c.last_message || <span className="text-zinc-600 italic">sem mensagens</span>}</div>
      </div>
      <div className="text-right flex-shrink-0">
        {c.last_message_at && (
          <div className="font-mono-accent text-[10px] text-zinc-500">
            {new Date(c.last_message_at).toLocaleDateString("pt-PT")}
          </div>
        )}
        {c.unread > 0 && (
          <div className="mt-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E63946] text-white text-[10px] font-bold">
            {c.unread}
          </div>
        )}
      </div>
    </Link>
  );
}
